import React from "react";
import { useTheme } from "@mui/material";

interface Props {
    width?: number;
    /**
     * Front glyph shown over the subscription cards:
     * - "bell": ringing bell — used when the user has no subscriptions at all.
     * - "search": magnifying glass — used when a search returned no matches.
     */
    variant?: "bell" | "search";
}

/**
 * Illustration for the subscriptions empty state: a miniature stack of
 * subscription cards (title line, breadcrumb line and channel chips) with a
 * glyph in front — mirroring the theme-colored, hand-drawn style of
 * {@link InstalledBaseEmptyIllustration} and ServiceReminderEmptyIllustration.
 *
 * Colors come from the application theme: the brand primary for the glyph,
 * neutral greys for the cards and lines.
 */
const NotificationSubscriptionsEmptyIllustration = ({ width = 200, variant = "bell" }: Props) => {
    const theme = useTheme();

    const backdrop = theme.palette.grey[100];
    const cardFill = theme.palette.common.white;
    const border = theme.palette.grey[300];
    const titleLine = theme.palette.grey[300];
    const subLine = theme.palette.grey[200];
    const primary = theme.palette.primary.main;
    const white = theme.palette.common.white;

    return (
        <svg
            width={width}
            viewBox="0 0 220 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            role="presentation"
            aria-hidden="true"
        >
            {/* soft backdrop */}
            <circle cx="110" cy="100" r="92" fill={backdrop} />

            {/* subscription cards: title + breadcrumb line + channel chips */}
            <g>
                <rect x="52" y="38" width="116" height="38" rx="8" fill={cardFill} stroke={border} strokeWidth="2" />
                <rect x="62" y="47" width="52" height="6" rx="3" fill={titleLine} />
                <rect x="62" y="58" width="72" height="5" rx="2.5" fill={subLine} />
                <rect x="138" y="46" width="20" height="9" rx="4.5" fill={subLine} />

                <rect x="52" y="84" width="116" height="38" rx="8" fill={cardFill} stroke={border} strokeWidth="2" />
                <rect x="62" y="93" width="44" height="6" rx="3" fill={titleLine} />
                <rect x="62" y="104" width="62" height="5" rx="2.5" fill={subLine} />
                <rect x="138" y="92" width="20" height="9" rx="4.5" fill={subLine} />

                <rect x="52" y="130" width="116" height="30" rx="8" fill={cardFill} stroke={border} strokeWidth="2" />
                <rect x="62" y="138" width="48" height="6" rx="3" fill={titleLine} />
                <rect x="62" y="149" width="34" height="5" rx="2.5" fill={subLine} />
            </g>

            {/* halo so the glyph reads as being in front of the cards */}
            <circle cx="148" cy="140" r="34" fill={white} />

            {variant === "bell" ? (
                <g
                    stroke={primary}
                    strokeWidth="4.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                >
                    {/* bell body */}
                    <path d="M148 119 c-9 0 -15 6 -15 15 c0 11 -4 15 -7 18 h44 c-3 -3 -7 -7 -7 -18 c0 -9 -6 -15 -15 -15 z" fill={primary} />
                    {/* top knob */}
                    <path d="M148 115 v4" />
                    {/* clapper */}
                    <path d="M142 152 a6 6 0 0 0 12 0" fill={white} stroke={primary} />
                    {/* ringing waves */}
                    <path d="M124 122 a30 30 0 0 0 -6 12" />
                    <path d="M172 122 a30 30 0 0 1 6 12" />
                </g>
            ) : (
                <>
                    {/* magnifying glass */}
                    <circle cx="143" cy="135" r="15" fill={white} stroke={primary} strokeWidth="5" />
                    <path d="M154 146 L169 161" stroke={primary} strokeWidth="6" strokeLinecap="round" />
                </>
            )}
        </svg>
    );
};

export default NotificationSubscriptionsEmptyIllustration;
